import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  editServiceType,
  type EditServiceTypeResult,
} from '../api/editServiceTypeApiService';
import { viewServiceType } from '../api/viewServiceTypeApiService';
import { SERVICE_TYPE_LABELS } from '../constants/labels';
import { ServiceTypeStatus } from '../types/serviceTypeTypes';
import { validateDescription, validateServiceName } from '../utils/validation';

export interface EditServiceTypeFormData {
  serviceName: string;
  description: string;
}

export interface EditServiceTypeFormErrors {
  serviceName?: string;
  description?: string;
}

const LIST_PATH = '/Typy-usług';

export const useEditServiceType = () => {
  const navigate = useNavigate();
  const { serviceTypeId = '' } = useParams<{ serviceTypeId: string }>();

  const [formData, setFormData] = useState<EditServiceTypeFormData>({
    serviceName: '',
    description: '',
  });
  const [initialData, setInitialData] = useState<EditServiceTypeFormData>({
    serviceName: '',
    description: '',
  });
  const [status, setStatus] = useState<ServiceTypeStatus>(
    ServiceTypeStatus.Active
  );
  const [errors, setErrors] = useState<EditServiceTypeFormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!serviceTypeId) {
        setIsLoading(false);
        setNotFound(true);
        return;
      }
      setIsLoading(true);
      setNotFound(false);
      const response = await viewServiceType(serviceTypeId);
      if (cancelled) {
        return;
      }
      if (response.success && response.data) {
        const next: EditServiceTypeFormData = {
          serviceName: response.data.service_name ?? '',
          description: response.data.description ?? '',
        };
        setFormData(next);
        setInitialData(next);
        setStatus(response.data.status);
        setLoadError(null);
      } else if (response.notFound) {
        setNotFound(true);
      } else {
        setLoadError(response.error ?? 'Failed to load service type');
      }
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [serviceTypeId]);

  const handleServiceNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 50);
      setFormData((prev) => ({ ...prev, serviceName: next }));
      if (errors.serviceName) {
        setErrors((prev) => {
          const { serviceName: _s, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.serviceName]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 500);
      setFormData((prev) => ({ ...prev, description: next }));
      if (errors.description) {
        setErrors((prev) => {
          const { description: _d, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.description]
  );

  const hasChanges = useMemo(() => {
    return (
      formData.serviceName.trim() !== initialData.serviceName.trim() ||
      formData.description.trim() !== initialData.description.trim()
    );
  }, [formData, initialData]);

  const applyFieldErrors = useCallback(
    (fieldErrors: Record<string, string>): boolean => {
      const mapped: EditServiceTypeFormErrors = {};
      if (fieldErrors['service_name']) {
        mapped.serviceName = fieldErrors['service_name'];
      }
      if (fieldErrors['description']) {
        mapped.description = fieldErrors['description'];
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return true;
      }
      return false;
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const nextErrors: EditServiceTypeFormErrors = {};
      const nameError = validateServiceName(formData.serviceName);
      if (nameError) {
        nextErrors.serviceName = nameError;
      }
      const descriptionError = validateDescription(formData.description);
      if (descriptionError) {
        nextErrors.description = descriptionError;
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const result: EditServiceTypeResult = await editServiceType(
          serviceTypeId,
          {
            serviceName: formData.serviceName.trim(),
            description: formData.description.trim(),
          }
        );

        if (result.success) {
          navigate(LIST_PATH, {
            state: { successMessage: SERVICE_TYPE_LABELS.EDIT_SUCCESS },
          });
          return;
        }

        const handledAsFieldError = result.fieldErrors
          ? applyFieldErrors(result.fieldErrors)
          : false;

        if (!handledAsFieldError) {
          setSnackbarError(result.error ?? '');
          setIsSnackbarOpen(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, serviceTypeId, navigate, applyFieldErrors]
  );

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate(LIST_PATH);
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(LIST_PATH);
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  return {
    serviceTypeId,
    formData,
    errors,
    status,
    setStatus,
    isLoading,
    isSubmitting,
    loadError,
    notFound,
    hasChanges,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleServiceNameChange,
    handleDescriptionChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
  };
};
